// src/components/resources/guides/ContentGuideGuide.tsx

'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ResourceLayout } from '~/components/resources/ResourceLayout'
import { RelatedResources } from '~/components/resources/RelatedResources'
import { ContentBlocks } from '~/components/resources/guides/content/ContentBlocks'
import { GlossaryFaqs } from '~/components/resources/glossary/GlossaryFaqs'
import type { ContentGuide } from '~/lib/resources/types/guides/content'
import { GUIDES_HUB_HERO } from '~/lib/resources/data/guides/images'
import { getResourceTopicBySlug } from '~/lib/resources/topics/registry'
import type { ResourceInstance } from '~/lib/resources/types/shared/base'
import { formatDate } from '~/lib/utils/date'
import { cn } from '~/lib/utils'
import RiTimeLine from '~/icons/remix/generated/RiTimeLine'
import RiBookOpenLine from '~/icons/remix/generated/RiBookOpenLine'

interface ContentGuideGuideProps {
  guide: ContentGuide
  relatedResources?: ResourceInstance[]
  /**
   * The "Updated on" date floored by the same resolver as the sitemap/JSON-LD
   * (`resolvedModifiedDate`), computed server-side in the route. When present it
   * supersedes the raw authored `guide.lastUpdated` so the visible date can never
   * read older than the structured-data `dateModified` on this page.
   */
  displayLastUpdated?: string
}

const DIFFICULTY_LABELS: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
}

const GUIDE_TYPE_LABELS: Record<string, string> = {
  'how-to': 'How-To Guide',
  article: 'Tutorial',
}

/**
 * Renders a structured how-to or tutorial content guide with hero, section nav,
 * rich content blocks, related resources, and optional FAQs.
 *
 * @param {ContentGuideGuideProps} props - Guide data, related resources, and layout props.
 * @returns {JSX.Element} Full content guide page body.
 */
export function ContentGuideGuide({
  guide,
  relatedResources = [],
  displayLastUpdated,
}: ContentGuideGuideProps): JSX.Element {
  const lastUpdated = displayLastUpdated ?? guide.lastUpdated
  const navSections = [
    ...guide.sections.map((section) => ({
      id: section.id ?? section.title.toLowerCase().replace(/\s+/g, '-'),
      label: section.title,
    })),
    ...(guide.faqs && guide.faqs.length > 0 ? [{ id: 'faq', label: 'FAQ' }] : []),
  ]

  const heroUrl =
    guide.images?.hero?.url ?? GUIDES_HUB_HERO
  const hasCustomArtwork = heroUrl !== GUIDES_HUB_HERO

  const topicLinks = (guide.topicSlugs ?? [])
    .map((slug) => getResourceTopicBySlug(slug))
    .filter((topic): topic is NonNullable<typeof topic> => topic !== undefined)

  return (
    <ResourceLayout showHeader={false} suppressFallbackHeading>
      <article className="space-y-14">
        {/* Hero */}
        <header
          className={cn(
            'relative overflow-hidden rounded-2xl border',
            hasCustomArtwork
              ? 'border-gray-200/80 bg-gray-950 dark:border-gray-800/80'
              : 'border-gray-200/60 dark:border-gray-800/60',
          )}
        >
          <div
            className={cn(
              'relative w-full',
              hasCustomArtwork ? 'aspect-[1.91/1] min-h-[220px]' : 'aspect-[21/9] min-h-[200px] sm:min-h-[280px]',
            )}
          >
            <Image
              src={heroUrl}
              alt={guide.images?.hero?.alt ?? guide.title}
              fill
              className={cn(
                hasCustomArtwork ? 'object-contain p-2 sm:p-4' : 'object-cover',
              )}
              priority
              sizes="(max-width: 768px) 100vw, 1200px"
            />
            {!hasCustomArtwork && (
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-gray-950/50 to-gray-950/20" />
            )}
          </div>

          <div
            className={cn(
              'space-y-4',
              hasCustomArtwork
                ? 'border-t border-gray-800/60 bg-gray-950 px-6 py-6 sm:px-10 sm:py-8'
                : 'absolute inset-x-0 bottom-0 p-6 sm:p-10',
            )}
          >
            <div className="flex flex-wrap items-center gap-3">
              {guide.category && (
                <span
                  className={cn(
                    'rounded-full px-3 py-1 text-sm font-medium backdrop-blur',
                    hasCustomArtwork
                      ? 'bg-indigo-500/20 text-indigo-300'
                      : 'bg-white/15 text-white',
                  )}
                >
                  {guide.category}
                </span>
              )}
              {guide.guideType && (
                <span
                  className={cn(
                    'rounded-full px-3 py-1 text-sm font-medium backdrop-blur',
                    hasCustomArtwork
                      ? 'bg-violet-500/20 text-violet-300'
                      : 'bg-indigo-500/80 text-white',
                  )}
                >
                  {GUIDE_TYPE_LABELS[guide.guideType] ?? guide.guideType}
                </span>
              )}
              {guide.difficulty && (
                <span
                  className={cn(
                    'rounded-full px-3 py-1 text-sm backdrop-blur',
                    hasCustomArtwork ? 'bg-white/10 text-gray-300' : 'bg-white/10 text-white/90',
                  )}
                >
                  {DIFFICULTY_LABELS[guide.difficulty] ?? guide.difficulty}
                </span>
              )}
            </div>

            <h1
              className={cn(
                'text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl',
                hasCustomArtwork ? 'text-white' : 'text-white',
              )}
            >
              {guide.canonicalTitle ?? guide.title}
            </h1>

            <p
              className={cn(
                'max-w-3xl text-lg',
                hasCustomArtwork ? 'text-gray-300' : 'text-gray-200',
              )}
            >
              {guide.description}
            </p>

            <div
              className={cn(
                'flex flex-wrap items-center gap-4 text-sm',
                hasCustomArtwork ? 'text-gray-400' : 'text-gray-300',
              )}
            >
              {lastUpdated && (
                <time dateTime={lastUpdated} className="flex items-center gap-1.5">
                  <RiTimeLine className="h-4 w-4" aria-hidden />
                  Updated {formatDate(lastUpdated)}
                </time>
              )}
              {guide.estimatedTime && (
                <span className="flex items-center gap-1.5">
                  <RiBookOpenLine className="h-4 w-4" aria-hidden />
                  {guide.estimatedTime} read
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Quick takeaway */}
        {guide.summary && (
          <div className="rounded-xl border border-indigo-200/70 bg-indigo-50/60 p-5 dark:border-indigo-800/40 dark:bg-indigo-950/30 sm:p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
              Quick takeaway
            </p>
            <p className="mt-2 text-base text-gray-700 dark:text-gray-300">{guide.summary}</p>
          </div>
        )}

        {/* Section navigation */}
        {navSections.length > 1 && (
          <nav aria-label="Guide sections" className="flex flex-wrap gap-2">
            {navSections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-indigo-500 dark:hover:bg-gray-800 dark:hover:text-indigo-300"
              >
                {section.label}
              </a>
            ))}
          </nav>
        )}

        {/* Content sections */}
        {guide.sections.map((section) => {
          const sectionId = section.id ?? section.title.toLowerCase().replace(/\s+/g, '-')

          return (
            <section key={sectionId} id={sectionId} className="scroll-mt-36 space-y-6">
              <div className="border-b border-gray-200/60 pb-4 dark:border-gray-800/60">
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
                  {section.title}
                </h2>
                {section.description && (
                  <p className="mt-2 max-w-3xl text-base text-gray-600 dark:text-gray-400">
                    {section.description}
                  </p>
                )}
              </div>

              {section.contentType === 'blocks' && section.content.blocks && (
                <ContentBlocks blocks={section.content.blocks} />
              )}
            </section>
          )
        })}

        {/* FAQs */}
        {guide.faqs && guide.faqs.length > 0 && (
          <section id="faq" className="scroll-mt-36 space-y-6">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Frequently Asked Questions
            </h2>
            <GlossaryFaqs
              items={guide.faqs
                .filter((faq): faq is typeof faq & { answer: string } => typeof faq.answer === 'string')
                .map((faq) => ({
                  question: faq.question,
                  answer: faq.answer,
                }))}
              idPrefix={guide.slug}
            />
          </section>
        )}

        {/* Related resources */}
        {relatedResources.length > 0 && (
          <section id="related-resources" className="scroll-mt-36">
            <RelatedResources
              resources={relatedResources}
              title="Related Tools & Guides"
              maxItems={6}
              titleLevel={2}
            />
          </section>
        )}

        {/* Topic hub links */}
        {topicLinks.length > 0 && (
          <section id="related-topics" className="scroll-mt-36 space-y-4">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Explore Topic Hubs
            </h2>
            <div className="flex flex-wrap gap-3">
              {topicLinks.map((topic) => (
                <Link
                  key={topic.id}
                  href={`/resources/topics/${topic.slug}`}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:border-indigo-500 dark:hover:bg-gray-800 dark:hover:text-indigo-300"
                >
                  {topic.title}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Responsible use footer for media-literacy guides */}
        {guide.tags?.includes('media-literacy') && (
          <footer className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-800 dark:bg-gray-900/50">
            <p className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Responsible Use
            </p>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              AdSights mockup generators are designed for legitimate marketing, education, and
              creative testing. Never use generated screenshots to impersonate real people,
              fabricate evidence, or deceive audiences. When sharing mockups, label them clearly
              as demonstrations. See our{' '}
              <Link href="/terms" className="text-indigo-600 hover:underline dark:text-indigo-400">
                Terms of Service
              </Link>{' '}
              for acceptable use.
            </p>
          </footer>
        )}
      </article>
    </ResourceLayout>
  )
}
